/**
 * SupplyChainPanel — suppliers, purchase orders, inventory & warehouse.
 * The tariff-exposure heart of the game.
 */

import { useMemo, useState } from 'react';
import { toast } from '@/hooks/useToast';
import { Lock, Ship, Warehouse } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Meter, PanelSection } from '@/components/game/ui';
import { useGameStore } from '@/store/gameStore';
import { money, num, formatGameDate } from '@/game/format';
import {
  CAPACITY_UPGRADE_COST_STEP,
  SUPPLIERS,
  TARIFF_LABELS,
} from '@/game/constants';
import { landedUnitCost } from '@/game/economy';
import { has } from '@/game/research';
import {
  actionExpandWarehouse,
  totalInventory,
  unitsOnOrder,
  warehouseCapacity,
} from '@/game/engine';

export function SupplyChainPanel() {
  const state = useGameStore((s) => s.state)!;
  const placePO = useGameStore((s) => s.placePO);
  const expandWarehouse = useGameStore((s) => s.expandWarehouse);

  const activeSkus = state.products.filter((p) => p.status === 'active');
  const [productId, setProductId] = useState(activeSkus[0]?.id ?? '');
  const [supplierId, setSupplierId] = useState(state.suppliersUnlocked[0] ?? 'foshan-foamworks');
  const [units, setUnits] = useState(200);

  const product = activeSkus.find((p) => p.id === productId) ?? activeSkus[0];
  const supplier = SUPPLIERS.find((x) => x.id === supplierId);

  const estimate = useMemo(() => {
    if (!product || !supplier) return null;
    return landedUnitCost(state, product, supplier.id);
  }, [state, product, supplier]);

  const inv = totalInventory(state);
  const onOrder = unitsOnOrder(state);
  const cap = warehouseCapacity(state);
  const expandCost = CAPACITY_UPGRADE_COST_STEP * (state.warehouseExpansions + 1);

  const submitPO = () => {
    if (!product || !supplier) return;
    const err = placePO(product.id, supplier.id, Math.floor(units));
    if (err) toast({ title: 'Purchase order rejected', description: err, variant: 'destructive' });
    else toast({ title: 'PO placed', description: `${Math.floor(units).toLocaleString()} units from ${supplier.name}.` });
  };

  return (
    <div className="space-y-6">
      {/* Warehouse */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Warehouse className="h-4 w-4" /> Warehouse</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Meter
            label={`${inv.toLocaleString()} in stock · ${onOrder.toLocaleString()} inbound`}
            value={cap > 0 ? ((inv + onOrder) / cap) * 100 : 0}
            colorClass={(inv + onOrder) / cap > 0.9 ? 'bg-red-500' : 'bg-amber-500'}
            valueText={`${cap.toLocaleString()} capacity`}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Holding cost: $0.055/unit/day{has(state, 'forecasting') ? ' (−25% forecasting)' : ''}</p>
            <Button size="sm" variant="outline" disabled={state.player.cash < expandCost} onClick={() => {
              const err = expandWarehouse();
              if (err) toast({ title: 'Expansion failed', description: err, variant: 'destructive' });
            }}>
              +1,600 capacity — {money(expandCost)}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Place PO */}
      <PanelSection title="Place Purchase Order" desc="Cash leaves the account the day you order — plan for the gap before sales replenish it.">
        <Card>
          <CardContent className="grid gap-3 py-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Product</Label>
              <Select value={product?.id} onValueChange={setProductId}>
                <SelectTrigger><SelectValue placeholder="Select SKU" /></SelectTrigger>
                <SelectContent>
                  {activeSkus.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Supplier</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {SUPPLIERS.filter((x) => state.suppliersUnlocked.includes(x.id)).map((x) => (
                    <SelectItem key={x.id} value={x.id}>{x.name} ({TARIFF_LABELS[x.country]})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="po-units">Units</Label>
              <Input id="po-units" type="number" min={supplier?.minOrderUnits ?? 50} step={25}
                value={units}
                onChange={(e) => setUnits(Number(e.target.value))}
              />
              {supplier && <p className="text-xs text-muted-foreground">MOQ {supplier.minOrderUnits}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Estimate</Label>
              <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                {estimate !== null ? (
                  <>
                    <p className="tabular-nums">{money(estimate)}/unit → <strong>{money(estimate * Math.max(0, Math.floor(units)))}</strong></p>
                    <p className="text-xs text-muted-foreground">{supplier?.leadTimeDays}-day lead · ×{state.tariffs[supplier!.country].toFixed(2)} duty</p>
                  </>
                ) : '—'}
              </div>
            </div>
          </CardContent>
          <CardContent className="pt-0">
            <Button className="w-full sm:w-auto" onClick={submitPO} disabled={!product || !supplier}>
              <Ship className="mr-1.5 h-4 w-4" /> Place order — pay {estimate ? money(estimate * Math.max(0, Math.floor(units))) : '—'} now
            </Button>
          </CardContent>
        </Card>
      </PanelSection>

      {/* Open POs */}
      {state.pos.length > 0 && (
        <PanelSection title={`In Transit (${state.pos.length})`}>
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Product</th>
                  <th className="px-4 py-2 font-medium">Supplier</th>
                  <th className="px-4 py-2 font-medium text-right">Units</th>
                  <th className="px-4 py-2 font-medium text-right">Paid</th>
                  <th className="px-4 py-2 font-medium text-right">ETA</th>
                </tr>
              </thead>
              <tbody>
                {state.pos.map((po) => {
                  const sup = SUPPLIERS.find((x) => x.id === po.supplierId);
                  const prod = state.products.find((x) => x.id === po.productId);
                  const daysLeft = po.etaDay - state.time.day;
                  return (
                    <tr key={po.id} className="border-b last:border-0">
                      <td className="px-4 py-2">{prod?.name ?? '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{sup?.name}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{num(po.units)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{money(po.unitCostEstimate * po.units)}</td>
                      <td className={cn('px-4 py-2 text-right tabular-nums', daysLeft <= 7 ? 'text-amber-600 dark:text-amber-400' : '')}>
                        {daysLeft}d ({formatGameDate(po.etaDay)})
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelSection>
      )}

      {/* Supplier directory */}
      <PanelSection title="Supplier Network" desc="Cheap Asia lanes carry trade-policy risk. Diversify before the petition, not after.">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {SUPPLIERS.map((sup) => {
            const unlocked = state.suppliersUnlocked.includes(sup.id);
            const gatedByResearch = sup.requiresResearch && !has(state, sup.requiresResearch);
            return (
              <Card key={sup.id} className={cn(!unlocked && 'opacity-60')}>
                <CardContent className="space-y-2 py-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold leading-tight">{sup.name}</p>
                    {!unlocked && (
                      <Badge variant="outline" className="shrink-0"><Lock className="mr-1 h-3 w-3" />{gatedByResearch ? 'Research' : 'Locked'}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{sup.blurb}</p>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs tabular-nums">
                    <Row k="Duty now" v={`×${state.tariffs[sup.country].toFixed(2)}`} danger={state.tariffs[sup.country] >= 1.8} />
                    <Row k="Cost" v={`×${sup.costMultiplier.toFixed(2)}`} />
                    <Row k="Lead" v={`${sup.leadTimeDays}d`} />
                    <Row k="Reliability" v={`${Math.round(sup.reliability * 100)}%`} />
                    <Row k="Defects" v={`${(sup.qualityVariance * 100).toFixed(1)}%`} />
                    <Row k="Trade risk" v={'●'.repeat(Math.round(sup.tariffRisk * 5))} danger={sup.tariffRisk >= 0.6} />
                  </dl>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </PanelSection>
    </div>
  );
}

function Row({ k, v, danger }: { k: string; v: string; danger?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={cn('font-medium', danger && 'text-red-600 dark:text-red-400')}>{v}</dd>
    </div>
  );
}
